import {
  useOrdersInfinite,
  useUserStatistics,
  type OrderSummary,
} from "@kupikupi/api";
import { formatPrice } from "@kupikupi/money";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TgBottomNav } from "../components/TgBottomNav";
import { TgEmpty } from "../components/TgEmpty";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { TgSkeleton } from "../components/TgSkeleton";
import { useAuth } from "../lib/auth";
import { miniDisplayCurrency, miniDisplayLocale } from "../lib/locale-currency";

const TAB_KEYS = [
  "all",
  "WAIT_PAYMENT",
  "PURCHASED",
  "WAIT_CONFIRM",
  "SHIPPED",
  "COMPLETED",
] as const;

const STATUS_STYLE: Record<string, { cls: string; emoji: string }> = {
  WAIT_PAYMENT: { cls: "warning", emoji: "⏱" },
  INIT: { cls: "warning", emoji: "⏱" },
  PURCHASING: { cls: "photo", emoji: "🛒" },
  PURCHASED: { cls: "photo", emoji: "🛒" },
  WAIT_CONFIRM: { cls: "photo", emoji: "📷" },
  SHIPPED: { cls: "transit", emoji: "✈" },
  COMPLETED: { cls: "delivered", emoji: "✓" },
  CANCELLED: { cls: "delivered", emoji: "✖" },
};

const TAB_I18N: Record<string, string> = {
  all: "all",
  WAIT_PAYMENT: "wait_payment",
  PURCHASED: "purchasing",
  WAIT_CONFIRM: "photo_check",
  SHIPPED: "in_transit",
  COMPLETED: "delivered",
};

// ─── Countdown hook ────────────────────────────────────────────────────────────
function useCountdown(targetMs: number | null | undefined): string | null {
  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (!targetMs) {
      setRemaining(null);
      return;
    }
    const tick = () => {
      const diff = targetMs - Date.now();
      setRemaining(diff > 0 ? diff : 0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetMs]);

  if (remaining === null || remaining <= 0) return null;
  const totalSec = Math.floor(remaining / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// ─── Status-aware subtitle ─────────────────────────────────────────────────────
function OrderSubtitle({ order }: { order: OrderSummary }) {
  const { t } = useTranslation("orders");
  const cancelTimer = useCountdown(
    order.status === "WAIT_PAYMENT" && order.expiredTime ? order.expiredTime : null,
  );

  if (order.status === "WAIT_PAYMENT") {
    if (cancelTimer) {
      return <span style={{ color: "var(--color-warning)" }}>{cancelTimer} {t("card.cancel_hint")}</span>;
    }
    return <span>{t("card.items_n", { n: order.totalQuantity })}</span>;
  }

  if (order.status === "WAIT_CONFIRM") {
    // step progress not available in list summary — show quantity
    return <span>{t("card.items_n", { n: order.totalQuantity })}</span>;
  }

  if (order.status === "COMPLETED") {
    // invite to rate
    return <span>{t("card.rate")}</span>;
  }

  return (
    <span>
      {t("card.items_n", { n: order.totalQuantity })}
      {order.primaryChannel ? ` · ${order.primaryChannel}` : ""}
    </span>
  );
}

// ─── Photo-check banner with auto-approve countdown ────────────────────────────
function PhotoCheckBanner({ orders }: { orders: OrderSummary[] }) {
  const { t } = useTranslation("orders");
  // Find earliest expiry among WAIT_CONFIRM orders to show meaningful countdown.
  // Backend doesn't expose photo auto-approve time in list summary, so we can
  // only show a generic count. Real auto-approve time lives in order-detail.
  // We show count + static copy matching design, without fabricating a deadline.
  const count = orders.filter((o) => o.status === "WAIT_CONFIRM" || o.hasPendingPhotoCheck).length;
  if (count === 0) return null;

  return (
    <div
      style={{
        background: "var(--color-sticker-yellow-soft)",
        border: "1px solid var(--color-sticker-yellow)",
        borderRadius: 10,
        padding: "10px 12px",
        fontSize: 11,
        color: "var(--color-sticker-yellow-fg)",
        display: "flex",
        gap: 8,
        alignItems: "center",
        margin: "8px 12px 0",
      }}
    >
      <span style={{ fontSize: 16 }}>📷</span>
      <span>
        <strong>
          {count} {t("photo_check.banner")}
        </strong>
      </span>
    </div>
  );
}

// ─── Tab counts from useUserStatistics ────────────────────────────────────────
function tabCount(
  tab: string,
  stats: { waitPaymentOrderQuantity?: number | null; waitConfirmOrderQuantity?: number | null; orderQuantity?: number | null } | undefined,
): number | null {
  if (!stats) return null;
  if (tab === "all") return stats.orderQuantity ?? null;
  if (tab === "WAIT_PAYMENT") return stats.waitPaymentOrderQuantity ?? null;
  if (tab === "WAIT_CONFIRM") return stats.waitConfirmOrderQuantity ?? null;
  return null;
}

// ─── Skeleton order card ───────────────────────────────────────────────────────
function OrderCardSkeleton() {
  return (
    <div
      style={{
        background: "#fff",
        borderBottom: "1px solid var(--color-border)",
        padding: "10px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <TgSkeleton width={100} height={11} />
        <TgSkeleton width={70} height={18} radius={8} />
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <TgSkeleton width={48} height={48} radius={8} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
          <TgSkeleton height={13} width="80%" />
          <TgSkeleton height={11} width="50%" />
        </div>
        <TgSkeleton width={60} height={16} radius={4} />
      </div>
    </div>
  );
}

// ─── Filter panel (bottom sheet style) ────────────────────────────────────────
const CHANNELS = ["all", "taobao", "tmall", "1688", "weidian", "pdd"] as const;
type ChannelFilter = (typeof CHANNELS)[number];

interface FilterState {
  channel: ChannelFilter;
  subject: string;
}

function FilterPanel({
  open,
  value,
  onApply,
  onClose,
}: {
  open: boolean;
  value: FilterState;
  onApply: (v: FilterState) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation("orders");
  const [draft, setDraft] = useState<FilterState>(value);
  useEffect(() => { if (open) setDraft(value); }, [open, value]);

  if (!open) return null;

  return (
    <>
      {/* backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,.35)",
          zIndex: 200,
        }}
      />
      {/* sheet */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: "#fff",
          borderRadius: "16px 16px 0 0",
          padding: "16px 16px 32px",
          zIndex: 201,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 14 }}>{t("toolbar.channel_label")}</div>

        {/* channel chips */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {CHANNELS.map((ch) => {
            const label = ch === "all" ? t("toolbar.channel_all") : t(`toolbar.channel_${ch}`);
            const active = draft.channel === ch;
            return (
              <button
                key={ch}
                type="button"
                onClick={() => setDraft((d) => ({ ...d, channel: ch }))}
                style={{
                  background: active ? "var(--color-text)" : "var(--color-surface)",
                  color: active ? "#fff" : "var(--color-text-muted)",
                  padding: "5px 12px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 600,
                  border: "1px solid var(--color-border)",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* keyword search */}
        <div style={{ fontWeight: 700, fontSize: 14 }}>{t("toolbar.search_ph")}</div>
        <input
          type="search"
          placeholder={t("toolbar.search_ph")}
          value={draft.subject}
          onChange={(e) => setDraft((d) => ({ ...d, subject: e.target.value }))}
          style={{
            width: "100%",
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid var(--color-border)",
            fontSize: 12,
            background: "var(--color-surface)",
            color: "var(--color-text)",
            boxSizing: "border-box",
          }}
        />

        <button
          type="button"
          onClick={() => { onApply(draft); onClose(); }}
          style={{
            background: "var(--color-cta)",
            color: "#fff",
            padding: "10px 0",
            borderRadius: 10,
            fontWeight: 700,
            fontSize: 13,
            border: 0,
          }}
        >
          {t("toolbar.channel_label")} ✓
        </button>
      </div>
    </>
  );
}

// ─── Load-more sentinel (IntersectionObserver) ────────────────────────────────
function LoadMoreSentinel({ onVisible }: { onVisible: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onVisible();
      },
      { threshold: 0.1 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [onVisible]);
  return <div ref={ref} style={{ height: 1 }} />;
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export function OrdersPage() {
  const { i18n, t } = useTranslation("orders");
  const displayLocale = miniDisplayLocale(i18n.language);
  const displayCurrency = miniDisplayCurrency(i18n.language);
  const { token: authToken, status: authStatus } = useAuth();

  const [tab, setTab] = useState<string>("all");
  const [filterOpen, setFilterOpen] = useState(false);
  const [filter, setFilter] = useState<FilterState>({ channel: "all", subject: "" });

  const buildInput = () => ({
    ...(tab !== "all" ? { status: tab } : {}),
    ...(filter.channel !== "all" ? { channel: filter.channel } : {}),
    ...(filter.subject.trim() ? { subject: filter.subject.trim() } : {}),
    pageSize: 10,
  });

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useOrdersInfinite(authToken, buildInput());

  const { data: stats } = useUserStatistics(authToken);

  const orders = data?.orders ?? [];

  const onFetchMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // ── booting state ──
  if (authStatus === "booting") {
    return (
      <TgPage tabbar>
        <TgHeader title={t("head.title")} back />
        <div style={{ padding: "16px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
          {[1, 2, 3].map((n) => <OrderCardSkeleton key={n} />)}
        </div>
        <TgBottomNav />
      </TgPage>
    );
  }

  // ── signed-out state ──
  if (authStatus === "signed-out") {
    return (
      <TgPage tabbar>
        <TgHeader title={t("head.title")} back />
        <TgEmpty
          icon="🔐"
          title={t("state.signin_t")}
          hint={t("state.signin_s")}
          cta={
            <Link
              to={"/profile" as never}
              style={{
                display: "inline-block",
                padding: "8px 18px",
                background: "var(--color-cta)",
                color: "#fff",
                borderRadius: 999,
                fontWeight: 800,
                fontSize: 12,
              }}
            >
              {t("state.signin_cta")}
            </Link>
          }
        />
        <TgBottomNav />
      </TgPage>
    );
  }

  return (
    <TgPage tabbar>
      <TgHeader
        title={t("head.title")}
        back
        right={
          <button
            type="button"
            onClick={() => setFilterOpen(true)}
            className="tg-header__r"
            style={{
              background: "transparent",
              border: 0,
              fontSize: 12,
              fontWeight: 600,
              color: "var(--color-text-muted)",
              cursor: "pointer",
              padding: "0 4px",
            }}
          >
            {t("toolbar.channel_label")}
          </button>
        }
      />

      {/* ── Status tabs ── */}
      <div
        style={{
          display: "flex",
          gap: 4,
          padding: "8px 12px",
          background: "#fff",
          borderBottom: "1px solid var(--color-border)",
          overflowX: "auto",
        }}
      >
        {TAB_KEYS.map((k) => {
          const isActive = tab === k;
          const count = tabCount(k, stats);
          const label = t(`tabs.${TAB_I18N[k] ?? "all"}` as never);
          return (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              style={{
                background: isActive ? "var(--color-text)" : "transparent",
                color: isActive ? "#fff" : "var(--color-text-muted)",
                padding: "6px 12px",
                borderRadius: 8,
                fontSize: 11,
                fontWeight: 700,
                border: 0,
                whiteSpace: "nowrap",
              }}
            >
              {label}
              {count != null && count > 0 ? ` ${count}` : ""}
            </button>
          );
        })}
      </div>

      <div className="tg-body" style={{ flex: 1 }}>
        {/* ── Photo-check banner ── */}
        <PhotoCheckBanner orders={orders} />

        {/* ── Loading skeletons ── */}
        {isLoading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {[1, 2, 3].map((n) => <OrderCardSkeleton key={n} />)}
          </div>
        )}

        {/* ── Error state ── */}
        {isError && (
          <div
            style={{
              padding: 32,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span style={{ fontSize: 32 }}>⚠️</span>
            <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>{t("state.error_t")}</div>
            <button
              type="button"
              onClick={() => refetch()}
              style={{
                padding: "7px 18px",
                background: "var(--color-cta)",
                color: "#fff",
                borderRadius: 999,
                fontWeight: 700,
                fontSize: 12,
                border: 0,
              }}
            >
              {t("state.error_retry")}
            </button>
          </div>
        )}

        {/* ── Empty state ── */}
        {!isLoading && !isError && orders.length === 0 && (
          <TgEmpty
            icon="📦"
            title={t("state.empty_t")}
            hint={t("state.empty_s")}
            cta={
              <Link
                to={"/catalog" as never}
                style={{
                  display: "inline-block",
                  padding: "8px 18px",
                  background: "var(--color-cta)",
                  color: "#fff",
                  borderRadius: 999,
                  fontWeight: 800,
                  fontSize: 12,
                }}
              >
                {t("state.empty_cta")}
              </Link>
            }
          />
        )}

        {/* ── Order cards ── */}
        {orders.map((o) => {
          const style = STATUS_STYLE[o.status] ?? STATUS_STYLE.WAIT_PAYMENT!;
          const statusLabel = t(`status.${o.status}` as never, { defaultValue: o.statusName ?? o.status });
          const first = o.items[0];
          const isWaitPayment = o.status === "WAIT_PAYMENT" || o.status === "INIT";

          return (
            <Link
              key={o.orderNo}
              to={"/order-detail/$orderNo" as never}
              params={{ orderNo: o.orderNo } as never}
              className="tg-order"
            >
              <div className="tg-order__top">
                <span className="tg-order__id">№ {o.orderNo}</span>
                <span
                  className={style.cls === "warning" ? "tg-st" : `tg-st tg-st--${style.cls}`}
                  style={
                    style.cls === "warning"
                      ? {
                          background: "var(--color-warning-soft)",
                          color: "var(--color-warning-foreground)",
                          border: "1px solid rgba(255,162,0,.3)",
                        }
                      : undefined
                  }
                >
                  <span
                    className="tg-st__dot"
                    style={style.cls === "warning" ? { background: "var(--color-warning)" } : undefined}
                  />
                  {style.emoji} {statusLabel}
                </span>
              </div>
              <div className="tg-order__body">
                <div className="tg-order__img" style={{ background: "var(--color-surface)" }}>
                  {first?.imageUrl ? (
                    <img
                      src={first.imageUrl}
                      alt={first.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 8,
                      }}
                    />
                  ) : (
                    "📦"
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="tg-order__t">
                    {first?.title ?? "—"}
                    {o.items.length > 1 && ` +${o.items.length - 1}`}
                  </div>
                  <div className="tg-order__s">
                    <OrderSubtitle order={o} />
                  </div>
                </div>
                <div
                  className="tg-order__price"
                  style={isWaitPayment ? { color: "var(--color-cta)" } : undefined}
                >
                  {formatPrice(o.orderAmountRubKopecks, displayLocale, displayCurrency)}
                </div>
              </div>
            </Link>
          );
        })}

        {/* ── Load-more sentinel ── */}
        {!isLoading && orders.length > 0 && (
          <LoadMoreSentinel onVisible={onFetchMore} />
        )}

        {/* ── Fetching more indicator ── */}
        {isFetchingNextPage && (
          <div
            style={{
              padding: "12px 0",
              textAlign: "center",
              color: "var(--color-text-muted)",
              fontSize: 11,
            }}
          >
            {t("state.loading")}
          </div>
        )}

        {/* ── All loaded indicator ── */}
        {!isLoading && !hasNextPage && orders.length > 0 && (
          <div
            style={{
              padding: "12px 0",
              textAlign: "center",
              color: "var(--color-text-muted)",
              fontSize: 10,
            }}
          >
            · {orders.length} ·
          </div>
        )}
      </div>

      {/* ── Filter panel ── */}
      <FilterPanel
        open={filterOpen}
        value={filter}
        onApply={setFilter}
        onClose={() => setFilterOpen(false)}
      />

      <TgBottomNav />
    </TgPage>
  );
}
